// Producer accounts are not created in this app — signup for them lives on
// theWorkbook producer site. Selecting "Producer" in the account dropdown
// mounts this notice, which hands the visitor over after a short delay.
//
// The timers live in an effect so switching back to another account type
// unmounts the component and cancels the pending navigation. The manual link
// covers anyone who does not want to wait, and browsers that block the
// scripted top-level navigation.

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { withI18n } from "react-i18next";

export const PRODUCER_REDIRECT_SECONDS = 10;

// Falls back to the international host so a missing env var still lands
// somewhere real instead of navigating to "undefined".
const PRODUCER_SITE_FALLBACK = "https://producer.thecwbint.com";

// Carries the incoming query string (utm_*, referrers, …) across to the
// producer site so attribution is not lost at the hand-off.
export const getProducerSiteUrl = (search?: string): string => {
  const base = process.env.REACT_APP_CWB_Producer_Site || PRODUCER_SITE_FALLBACK;
  const params = new URLSearchParams(search ? search.replace(/^\?/, "") : "");
  const allowedParams = new URLSearchParams();

  params.forEach((value, key) => {
    if (key === "referrer" || key.indexOf("utm_") === 0) {
      allowedParams.append(key, value);
    }
  });

  const qs = allowedParams.toString();

  if (!qs) return base;

  return `${base}${base.indexOf("?") === -1 ? "?" : "&"}${qs}`;
};

interface IProps {
  t?: any;
  location?: any;
}

// Exported unwrapped for tests, which supply `t` directly rather than
// booting i18n.
export const ProducerRedirectStep: React.FC<IProps> = ({ t, location }) => {
  const target = getProducerSiteUrl(location && location.search);
  const [remaining, setRemaining] = useState(PRODUCER_REDIRECT_SECONDS);

  useEffect(() => {
    // Derived from a start timestamp rather than a decrementing counter so a
    // backgrounded tab does not drift away from the redirect timer below.
    const startedAt = Date.now();
    const tick = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setRemaining(Math.max(0, PRODUCER_REDIRECT_SECONDS - elapsed));
    }, 250);

    const redirect = setTimeout(() => {
      window.top.location.href = target;
    }, PRODUCER_REDIRECT_SECONDS * 1000);

    return () => {
      clearInterval(tick);
      clearTimeout(redirect);
    };
  }, [target]);

  return (
    <Wrapper>
      <Title>{t("Producer sign-up is one click away")}</Title>
      <Message>
        {t(
          "We are taking you to theWorkbook producer site to finish creating your account."
        )}
      </Message>
      <Counter aria-live="polite">
        {t("Redirecting in {{seconds}}s", { seconds: remaining })}
      </Counter>
      <ContinueButton href={target} target="_top">
        {t("Go now")}
      </ContinueButton>
      <Hint>{t("Not a producer? Choose another account type above.")}</Hint>
    </Wrapper>
  );
};

export default withI18n()(ProducerRedirectStep);

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-top: 16px;
`;

const Title = styled.div`
  font-size: ${(p) => p.theme["lg-font-size"]};
  font-weight: ${(p) => p.theme["font-weight-600"]};
  color: ${(p) => p.theme.formField.color};
  line-height: 24px;
`;

const Message = styled.div`
  font-size: ${(p) => p.theme["font-size"]};
  color: ${(p) => p.theme.formField.color};
  line-height: 22px;
  margin-top: 12px;
`;

const Counter = styled.div`
  font-size: ${(p) => p.theme["font-size"]};
  font-weight: ${(p) => p.theme["font-weight-600"]};
  color: ${(p) => p.theme.btnBlue};
  margin: 24px 0;
`;

const ContinueButton = styled.a`
  width: 100%;
  display: flex;
  justify-content: center;

  font-size: ${(p) => p.theme["font-size"]};
  font-weight: ${(p) => p.theme["font-weight-600"]};
  background-color: ${(p) => p.theme.btnBlue};
  border-radius: 4px;
  padding: 9px 0;

  &,
  &:link,
  &:visited,
  &:hover {
    color: ${(p) => p.theme.white} !important;
    text-decoration: none;
  }
`;

const Hint = styled.div`
  font-size: ${(p) => p.theme["xxs-font-size"]};
  color: ${(p) => p.theme.placeHolderColor};
  line-height: 16px;
  margin-top: 16px;
`;
